import { Injectable } from '@angular/core';
import { ApiRequestService } from '../../shared/api-request.service';

/**
 * A Personal API token as returned by the listing and create endpoints.
 */
export interface ApiToken {
  id: number;
  name: string;
  created_at: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  never_expires: boolean;
}

/**
 * The create response additionally carries the one-time plaintext token value.
 */
export interface CreatedApiToken extends ApiToken {
  plaintext_token: string;
}

/**
 * Payload sent when creating a Personal API token.
 */
export interface CreateApiTokenPayload {
  name: string;
  never_expires: boolean;
  expires_at?: string;
}

/**
 * Service for managing the authenticated user's Personal API tokens.
 * Wraps the three v2/user/api-tokens endpoints.
 */
@Injectable({
  providedIn: 'root'
})
export class ApiTokensService {

  constructor(private api: ApiRequestService) {}

  /**
   * List the user's Personal API tokens.
   */
  list(): Promise<ApiToken[]> {
    return this.api.makeRequest('get', 'v2/user/api-tokens')
      .then((response: any) => response.data as ApiToken[]);
  }

  /**
   * Create a new Personal API token. The plaintext value is returned exactly once.
   */
  create(payload: CreateApiTokenPayload): Promise<CreatedApiToken> {
    return this.api.makeRequest('post', 'v2/user/api-tokens', payload)
      .then((response: any) => response.data as CreatedApiToken);
  }

  /**
   * Revoke (permanently delete) a Personal API token the user owns.
   */
  revoke(id: number): Promise<void> {
    return this.api.makeRequest('delete', `v2/user/api-tokens/${id}`)
      .then(() => undefined);
  }
}
